import { WasteScheduleAdherenceIssueCode } from "generated-graphql/graphql";
import { formatInvoiceDate } from "routes/Customer/Waste/Invoices/utils";

import { ExceptionRow } from "./columns";
import {
  describeChargeDates,
  formatDaysOutside,
  formatGradedSpan,
  formatWindow,
  KIND_LABELS,
  monthDay,
  SCHEDULE_TYPE_LABELS,
  serviceLabel,
  statedScheduleTypes,
  UNKNOWN_KIND_LABEL,
  vendorTermAddsDetail,
} from "./labels";

type InvoiceLine = ExceptionRow["billedLines"][number];

/** One side's value. Null when that side has nothing to say here. */
export type ComparisonCell = { text: string; caption: string | null } | null;

export type ComparisonRow = {
  label: string;
  schedule: ComparisonCell;
  invoice: ComparisonCell;
  /** The two records disagree on this, which is where a reader should look. */
  differs: boolean;
};

/**
 * What the invoices say about a result: the paired visit, or — for a window
 * billed against but paired with nothing — the charges billed to its order.
 */
type InvoiceEvidence = {
  lines: readonly InvoiceLine[];
  date: string;
  service: string;
  orderNumber: string | null;
  store: string | null;
};

const cell = (text: string | null | undefined, caption: string | null = null): ComparisonCell =>
  text == null || text === "" ? null : { text, caption };

const distinct = (values: readonly (string | null | undefined)[]) => [
  ...new Set(values.filter((value): value is string => value != null && value !== "")),
];

export function invoiceEvidence(row: ExceptionRow): InvoiceEvidence | null {
  const { visit } = row;
  if (visit != null) {
    return {
      lines: visit.invoiceLines,
      date:
        visit.serviceDate == null
          ? describeChargeDates(visit.invoiceLines)
          : formatInvoiceDate(visit.serviceDate),
      service: row.kind ? KIND_LABELS[row.kind] : UNKNOWN_KIND_LABEL,
      orderNumber: visit.serviceOrderNumber ?? null,
      store: row.customerFacilityId ?? null,
    };
  }
  const lines = row.billedLines;
  if (lines.length === 0) return null;
  return {
    lines,
    date: describeChargeDates(lines),
    service: distinct(lines.map((line) => serviceLabel(line.serviceKind, false))).join(", "),
    // These are the charges billed to the window's own order.
    orderNumber: row.occurrence?.vendorServiceOrderNumber ?? null,
    store: distinct(lines.map((line) => line.customerFacilityId)).join(", ") || null,
  };
}

function bothDiffer(schedule: ComparisonCell, invoice: ComparisonCell): boolean {
  return schedule != null && invoice != null && schedule.text !== invoice.text;
}

/**
 * Whether no charge billed to an unpaired window falls inside it, which is the
 * plainest reason nothing paired. A graded visit states this as its timing.
 */
function billedOutsideWindow(row: ExceptionRow, lines: readonly InvoiceLine[]): boolean {
  const end = row.occurrence?.scheduledWindowEnd?.slice(0, 10);
  if (end == null) return false;
  const start = row.occurrence?.scheduledWindowStart?.slice(0, 10);
  const days = lines.flatMap((line) => (line.serviceDate ? [line.serviceDate.slice(0, 10)] : []));
  return days.length > 0 && days.every((day) => day > end || (start != null && day < start));
}

function dateRow(row: ExceptionRow, evidence: InvoiceEvidence | null): ComparisonRow {
  const start = row.occurrence?.scheduledWindowStart;
  const end = row.occurrence?.scheduledWindowEnd;
  // A window named by its last day is graded across the days before it too.
  const graded =
    start && end && formatGradedSpan(start, end) != null
      ? `Graded against ${monthDay(start)} – ${monthDay(end)}`
      : null;
  return {
    label: "Date",
    schedule: row.occurrence == null ? null : cell(formatWindow(start, end), graded),
    invoice: cell(evidence?.date),
    differs:
      row.visit == null
        ? billedOutsideWindow(row, evidence?.lines ?? [])
        : row.daysOutside != null && row.daysOutside !== 0,
  };
}

/** The vendor's terms on the charges, where they say more than our category. */
function billedTerms(evidence: InvoiceEvidence): string | null {
  const terms = distinct(evidence.lines.map((line) => line.sourceServiceType)).filter((term) =>
    vendorTermAddsDetail(evidence.service, term)
  );
  return terms.length === 0 ? null : `Billed as ${terms.join(", ")}`;
}

/**
 * Flagged wherever the audit found the two services differ, including two
 * special collections that share our category and differ only by the
 * vendor's term.
 */
function serviceRow(row: ExceptionRow, evidence: InvoiceEvidence | null): ComparisonRow {
  const { occurrence } = row;
  const kind = occurrence == null ? null : KIND_LABELS[occurrence.kind];
  const term = occurrence?.serviceType;
  const schedule =
    kind == null ? null : cell(kind, vendorTermAddsDetail(kind, term) ? `Listed as ${term}` : null);
  const invoice = evidence == null ? null : cell(evidence.service, billedTerms(evidence));
  const auditDisagrees = row.issues.some(
    (issue) => issue.code === WasteScheduleAdherenceIssueCode.ServiceKindDisagreement
  );
  return {
    label: "Service",
    schedule,
    invoice,
    differs: bothDiffer(schedule, invoice) || auditDisagrees,
  };
}

/** Only where the charges state an arrangement; most say nothing either way. */
function arrangementRow(row: ExceptionRow, evidence: InvoiceEvidence | null): ComparisonRow | null {
  const stated = statedScheduleTypes(evidence?.lines ?? []);
  if (stated.length === 0) return null;
  const scheduled = row.occurrence?.scheduleType;
  return {
    label: "Arranged as",
    schedule: scheduled == null ? null : cell(SCHEDULE_TYPE_LABELS[scheduled]),
    invoice: cell(
      stated.map((type) => SCHEDULE_TYPE_LABELS[type]).join(", "),
      stated.length > 1 ? "The charges disagree" : null
    ),
    differs: scheduled != null && !(stated.length === 1 && stated[0] === scheduled),
  };
}

function orderRow(row: ExceptionRow, evidence: InvoiceEvidence | null): ComparisonRow | null {
  const schedule = cell(row.occurrence?.vendorServiceOrderNumber);
  const invoice = cell(evidence?.orderNumber);
  if (schedule == null && invoice == null) return null;
  return { label: "Order number", schedule, invoice, differs: bothDiffer(schedule, invoice) };
}

/**
 * The audit refuses to pair records whose store numbers conflict once written
 * the same way, so two spellings on a paired result are one store (0618 and
 * 618). On an unpaired result a different store is a real difference.
 */
function storeRow(row: ExceptionRow, evidence: InvoiceEvidence | null): ComparisonRow | null {
  const scheduled = row.occurrence?.customerFacilityId;
  const billed = evidence?.store;
  if (scheduled == null && billed == null) return null;
  const paired = row.occurrence != null && row.visit != null;
  const spelledTwoWays = paired && scheduled != null && billed != null && scheduled !== billed;
  const schedule = cell(scheduled);
  const invoice = cell(billed, spelledTwoWays ? "Same store, written differently" : null);
  return { label: "Store", schedule, invoice, differs: !paired && bothDiffer(schedule, invoice) };
}

/** The schedule set beside the invoices, one fact per row. */
export function comparisonRows(row: ExceptionRow): ComparisonRow[] {
  const evidence = invoiceEvidence(row);
  return [
    dateRow(row, evidence),
    serviceRow(row, evidence),
    arrangementRow(row, evidence),
    orderRow(row, evidence),
    storeRow(row, evidence),
  ].filter((entry) => entry != null);
}

/** The timing, where it was graded: the one figure a reader came for. */
export function timingHeadline(row: ExceptionRow): { text: string } | null {
  if (row.daysOutside == null) return null;
  return { text: formatDaysOutside(row.daysOutside) };
}
